import React, { useState } from 'react';
import { Table, Button, Space, Select, Upload, message, Modal, List, Typography } from 'antd';
import { SearchOutlined, FileTextOutlined, ImportOutlined } from '@ant-design/icons';
import { getAirwayBillColumns } from './airwayBillColumns';
import AirwayBillIconView from './AirwayBillIconView';
import { 
  NavigationBreadcrumb, 
  SearchHeader, 
  ViewModeToggle 
} from '../../common';

const statusConfig = {
  'SHIPPING': { label: '运输中', color: '#1890ff' },
  'PENDING_REVIEW': { label: '未审单', color: '#1890ff' },
  'PENDING_CUSTOMS': { label: '待清关', color: '#1890ff' },
  'PICKED_UP': { label: '已提货未过机', color: '#1890ff' },
  'INSPECTED_PENDING': { label: '已出查验未放行', color: '#1890ff' },
  'RELEASED': { label: '已放行', color: '#1890ff' }
};

const AirwayBillList = ({
  airwayBills,
  loading,
  onEdit,
  onDelete,
  onCreate,
  onRowClick,
  onNavigateToAirwayBills,
  onSearch,
  searchText,
  viewMode,
  onViewModeChange,
  customers,
  selectedCustomer,
  onCustomerChange,
}) => {
  const [filters, setFilters] = React.useState({
    customer: selectedCustomer,
    is_additional_tax_notes: undefined,
    is_claim_package_notes: undefined,
    selectedStatuses: [],
    showUnprocessed: false
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    
    // If it's a customer filter, trigger both customer change and search
    if (field === 'customer') {
      onCustomerChange(value);
      onSearch({ search: searchText, ...newFilters });
    } else {
      // For boolean filters, pass them through the search function
      onSearch({ search: searchText, ...newFilters });
    }
  };

  const handleStatusFilterChange = async (status) => {
    const newFilters = { 
      ...filters, 
      selectedStatuses: filters.selectedStatuses.includes(status) ? [] : [status],
      showUnprocessed: false
    };
    setFilters(newFilters);
    // Call backend API with selected status
    await onSearch({ search: searchText, ...newFilters, status: newFilters.selectedStatuses });
  };

  const handleShowAll = async () => {
    const newFilters = {
      ...filters,
      selectedStatuses: [],
      showUnprocessed: false
    };
    setFilters(newFilters);
    await onSearch({ search: searchText, ...newFilters });
  };

  const handleShowUnprocessed = async () => {
    const newFilters = {
      ...filters,
      selectedStatuses: [],
      showUnprocessed: !filters.showUnprocessed
    };
    setFilters(newFilters);
    await onSearch({ search: searchText, ...newFilters, is_unprocessed: !filters.showUnprocessed });
  };

  const handleDelete = (record) => {
    onDelete(record);
  };

  const handleSearch = ({ searchText, dateRange, dateField, dateFrom, dateTo }) => {
    // Format date range for backend
    let searchParams = { search: searchText };
    
    // Handle date range from RangePicker
    if (dateRange && dateRange.length === 2) {
      searchParams.date_from = dateRange[0].format('YYYY-MM-DD');
      searchParams.date_to = dateRange[1].format('YYYY-MM-DD');
      searchParams.date_field = dateField || 'created_at'; // Include the selected date field
    }
    
    // Handle individual date parameters (fallback)
    if (dateFrom && dateTo) {
      searchParams.date_from = dateFrom;
      searchParams.date_to = dateTo;
      searchParams.date_field = dateField || 'created_at'; // Include the selected date field
    }
    
    console.log('DEBUG: Final searchParams being sent to backend:', searchParams);
    onSearch(searchParams);
  };

  const handleClear = async () => {
    const newFilters = {
      customer: undefined,
      is_additional_tax_notes: undefined,
      is_claim_package_notes: undefined,
      public_status: undefined,
      is_processed: undefined,
      selectedStatuses: [],
      showUnprocessed: false
    };
    setFilters(newFilters);
    await onSearch({ search: '', ...newFilters });
    onCustomerChange(undefined);  // Clear customer filter
  };

  const columns = getAirwayBillColumns({
    handleDelete,
    selectedCustomer: filters,
    onCustomerChange: handleFilterChange,
    customers
  });

  const filteredBills = filters.showUnprocessed 
    ? airwayBills.filter(bill => !bill.is_processed && (bill.is_additional_tax_notes || bill.is_claim_package_notes))
    : filters.selectedStatuses.length > 0 
      ? airwayBills.filter(bill => {
          // If we're filtering for RELEASED status, include bills where either status or public_status is RELEASED
          if (filters.selectedStatuses.includes('RELEASED')) {
            return bill.status === 'RELEASED' || bill.public_status === 'RELEASED';
          }
          // For other statuses, use the regular status field
          return filters.selectedStatuses.includes(bill.status);
        })
      : airwayBills;

  return (
    <div style={{ height: '100%' }}>
      {/* Main content */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%', padding: '16px' }}>
          {/* Status Summary Section */}
          <div style={{ 
            padding: '8px',
            background: '#f0f7ff',
            borderRadius: '8px',
            border: '1px solid #d6e8ff',
            boxShadow: '0 2px 4px rgba(24,144,255,0.1)'
          }}>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div 
                style={{ 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#1890ff',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onClick={handleShowAll}
              >
                <span style={{ fontSize: '20px', color: '#1890ff' }}>{airwayBills.length}</span>
                合计批次
              </div>
              <div 
                style={{ 
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1890ff',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onClick={handleShowUnprocessed}
              >
                <span style={{ fontSize: '20px', color: '#1890ff' }}>
                  {airwayBills.filter(bill => 
                    !bill.is_processed && 
                    (bill.is_additional_tax_notes || bill.is_claim_package_notes)
                  ).length}
                </span>
                账单未核对
              </div>
              {Object.entries(statusConfig).map(([status, config]) => {
                const count = airwayBills.filter(bill => bill.status === status).length;
                if (count === 0) return null;
                return (
                  <div key={status} style={{ 
                    fontSize: '14px',
                    color: config.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    backgroundColor: filters.selectedStatuses.includes(status) ? `${config.color}10` : 'transparent',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontWeight: filters.selectedStatuses.includes(status) ? '600' : 'normal'
                  }}
                  onClick={() => handleStatusFilterChange(status)}
                  >
                    <span style={{ fontSize: '20px', fontWeight: '600' }}>{count}</span>
                    {config.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center',
            backgroundColor: '#fff',
            zIndex: 1,
            flexShrink: 0,
            position: 'sticky',
            top: 0
          }}>
            <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
              <SearchHeader
                searchConfig={{
                  text: true,
                  date: true
                }}
                onSearch={handleSearch}
                onClear={handleClear}
                loading={loading}
                value={searchText}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0, minWidth: 'max-content' }}>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  const formData = new FormData();
                  formData.append('file', file);
                  
                  console.log('Starting import of Excel file:', file.name);
                  message.loading({ content: '正在导入...', key: 'importLoading', duration: 0 });
                  
                  fetch('/api/orders/import_airway_bills/', {
                    method: 'POST',
                    body: formData,
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  })
                  .then(response => response.json())
                  .then(data => {
                    console.log('Import response:', data);
                    message.destroy('importLoading');
                    
                    if (data.error) {
                      console.error('Import failed:', data.error);
                      message.error({ content: data.error, duration: 5 });
                    } else {
                      // Show detailed modal with results
                      Modal.success({
                        title: '导入完成',
                        width: 600,
                        content: (
                          <div>
                            <p>总记录数: {data.total_records}</p>
                            <p>成功导入: {data.successful_count} 条</p>
                            <p>更新记录: {data.duplicate_count} 条</p>
                            <p>错误记录: {data.error_count} 条</p>
                            {data.error_count > 0 && (
                              <div style={{ marginTop: '16px' }}>
                                <h4>错误详情:</h4>
                                <List
                                  size="small"
                                  dataSource={data.errors}
                                  renderItem={error => (
                                    <List.Item>
                                      <Typography.Text type="danger">
                                        第 {error.row} 行 ({error.airway_bill_number}): {error.error}
                                      </Typography.Text>
                                    </List.Item>
                                  )}
                                />
                              </div>
                            )}
                            {data.duplicate_count > 0 && (
                              <div style={{ marginTop: '16px' }}>
                                <h4>更新记录:</h4>
                                <List
                                  size="small"
                                  dataSource={data.duplicates}
                                  renderItem={number => (
                                    <List.Item>
                                      <Typography.Text type="warning">
                                        {number}
                                      </Typography.Text>
                                    </List.Item>
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        ),
                        onOk: () => {
                          // Refresh the list
                          window.location.reload();
                        }
                      });
                    }
                  })
                  .catch(error => {
                    console.error('Import error:', error);
                    message.destroy('importLoading');
                    message.error({ content: '导入失败: ' + error.message, duration: 5 });
                  });
                  return false;
                }}
              >
                <Button icon={<ImportOutlined />}>导入EXCEL</Button>
              </Upload>
              <Button type="primary" onClick={onCreate}>新增空运单</Button>
              <ViewModeToggle
                value={viewMode}
                onChange={onViewModeChange}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto', marginTop: '8px', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            {viewMode === 'list' ? (
              <Table
                size="small"
                columns={columns}
                dataSource={filteredBills}
                rowKey="id"
                loading={loading}
                onRow={(record) => ({
                  onClick: () => onRowClick(record),
                  style: { cursor: 'pointer' }
                })}
                className="compact-table hover-highlight"
                style={{ 
                  fontSize: '12px'
                }}
                pagination={{
                  total: filteredBills.length,
                  pageSize: 50,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 个空运单`
                }}
              />
            ) : (
              <AirwayBillIconView
                airwayBills={filteredBills}
                onEdit={onEdit}
                onDelete={handleDelete}
                onRowClick={onRowClick}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add CSS for compact table and customer filter
const style = document.createElement('style');
style.textContent = `
  .compact-table .ant-table-cell {
    padding: 8px 12px !important;
  }
  .compact-table .ant-table-thead .ant-table-cell {
    padding: 8px 8px !important;
    background: #fafafa;
    font-weight: 600;
    color: #1f2937;
    font-size: 12px;
    white-space: nowrap;
  }
  .hover-highlight .ant-table-tbody > tr:hover > td {
    background: #f0f7ff !important;
  }
  .ant-table-row {
    transition: all 0.3s;
  }
  .ant-table-row:nth-child(even) {
    background-color: #fafafa;
  }
  .ant-tag {
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 500;
  }
  .ant-table-pagination {
    margin: 16px 0 0 0 !important;
  }
  .ant-table {
    border-radius: 8px;
    overflow: hidden;
  }
`;
document.head.appendChild(style);

export default AirwayBillList;
